import type { ApiClient } from '@carbonwebmail/api-client'
import type { SearchHitDto } from '@carbonwebmail/contracts'
import { useCallback, useEffect, useState } from 'react'

/**
 * The shell's search, as somebody types.
 *
 * Three decisions, and all of them are about not lying while a request is in
 * flight.
 *
 * **It waits before asking.** A request per keystroke would be nine requests to
 * find "northwind", eight of them already stale by the time they answer. A
 * short pause after typing stops is the whole of it.
 *
 * **A late answer is discarded.** Requests can finish out of order, and a slow
 * answer for "nor" arriving after a fast one for "northwind" would replace the
 * right results with older ones. Each request remembers the term it was for and
 * only the current one is allowed to write.
 *
 * **An empty box is not an empty result.** They look identical on screen and
 * mean opposite things, so nothing is shown until there is a term and an
 * answer for it.
 */

const SETTLE_MS = 200

export type SearchState =
  /** Nothing typed. The dropdown is not open. */
  | { readonly status: 'idle' }
  | { readonly status: 'searching' }
  | { readonly status: 'found'; readonly hits: readonly SearchHitDto[] }
  /** The request failed. Said, rather than shown as "nothing matched". */
  | { readonly status: 'failed' }

export function useSearch({ client }: { readonly client: ApiClient }) {
  const [term, setTerm] = useState('')
  const [state, setState] = useState<SearchState>({ status: 'idle' })

  useEffect(() => {
    const wanted = term.trim()
    if (wanted === '') {
      setState({ status: 'idle' })
      return
    }

    setState({ status: 'searching' })
    // `current` closes over this effect's run. A later keystroke cancels the
    // timer and starts a new one, and an answer that arrives after this effect
    // has been cleaned up finds `current` false and writes nothing.
    let current = true

    const timer = setTimeout(() => {
      client
        .findAnything(wanted)
        .then((results) => {
          if (current) setState({ status: 'found', hits: results.hits })
        })
        .catch(() => {
          if (current) setState({ status: 'failed' })
        })
    }, SETTLE_MS)

    return () => {
      current = false
      clearTimeout(timer)
    }
  }, [client, term])

  /** Closes the dropdown after a hit is followed, so it is not still open on arrival. */
  const clear = useCallback(() => {
    setTerm('')
    setState({ status: 'idle' })
  }, [])

  return { term, setTerm, state, clear }
}
